import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import HorizonLogo from "./svg/HorizonLogo";
import { Toaster } from "./toaster";

interface LayoutProps {
  children: ReactNode;
}

const ENTRY_POINT_HOST_SELECTOR = "[data-horizon-ep]";
const DEFAULT_BACKGROUND_CLASS = "bg-gradient-primary";

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizeEntryPointId(value?: string | null) {
  const normalized = value ? safeDecodeURIComponent(value).trim().replace(/^#/, "") : "";
  return normalized || null;
}

function getPathEntryPointCandidates(pathname: string) {
  return new Set(
    pathname
      .split("/")
      .filter(Boolean)
      .map(normalizeEntryPointId)
      .filter(Boolean)
  );
}

function normalizeBackground(value?: string | null) {
  const background = value?.trim();

  if (
    !background ||
    background === "transparent" ||
    background === "rgba(0, 0, 0, 0)"
  ) {
    return null;
  }

  return background;
}

function findCurrentEntryPointHost(surface: HTMLElement, pathname: string) {
  const hosts = Array.from(
    surface.querySelectorAll<HTMLElement>(ENTRY_POINT_HOST_SELECTOR)
  );

  if (hosts.length === 0) return null;

  const pathCandidates = getPathEntryPointCandidates(pathname);

  const matchingHost = hosts.find((host) => {
    const hostEntryPointId = normalizeEntryPointId(host.dataset.horizonEp);
    return hostEntryPointId ? pathCandidates.has(hostEntryPointId) : false;
  });

  return matchingHost ?? null;
}

function readBackgroundFromShadowRoot(shadowRoot: ShadowRoot) {
  const backgroundElement = Array.from(
    shadowRoot.querySelectorAll<HTMLElement>("[style]")
  ).find((element) => Boolean(element.style.backgroundColor));

  return normalizeBackground(backgroundElement?.style.backgroundColor);
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [surfaceBackground, setSurfaceBackground] = useState<string | null>(
    null
  );

  useEffect(() => {
    setSurfaceBackground(null);

    let frameId: number | null = null;
    let shadowObserver: MutationObserver | null = null;
    let observedShadowRoot: ShadowRoot | null = null;
    let retryCount = 0;

    const setBackground = (background: string | null) => {
      setSurfaceBackground((currentBackground) =>
        currentBackground === background ? currentBackground : background
      );
    };

    const readCurrentBackground = () => {
      const surface = surfaceRef.current;
      if (!surface) {
        setBackground(null);
        return;
      }

      const host = findCurrentEntryPointHost(surface, location.pathname);
      const shadowRoot = host?.shadowRoot;

      if (!shadowRoot) {
        setBackground(null);
        return;
      }

      setBackground(readBackgroundFromShadowRoot(shadowRoot));
    };

    const scheduleRead = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(readCurrentBackground);
    };

    const attachShadowObserver = () => {
      const surface = surfaceRef.current;
      if (!surface) return;

      const host = findCurrentEntryPointHost(surface, location.pathname);
      const shadowRoot = host?.shadowRoot;

      if (!shadowRoot) return;

      if (observedShadowRoot === shadowRoot) return;

      shadowObserver?.disconnect();
      observedShadowRoot = shadowRoot;

      shadowObserver = new MutationObserver(scheduleRead);

      shadowObserver.observe(shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });

      scheduleRead();
    };

    const sync = () => {
      attachShadowObserver();
      scheduleRead();
    };

    const surfaceObserver = new MutationObserver(sync);

    if (surfaceRef.current) {
      surfaceObserver.observe(surfaceRef.current, {
        childList: true,
        subtree: true,
      });
    }

    const handleEntryPointReady = () => {
      sync();
    };

    window.addEventListener("horizon-entry-point-ready", handleEntryPointReady);

    const retryUntilReady = window.setInterval(() => {
      retryCount += 1;
      sync();

      if (observedShadowRoot || retryCount > 100) {
        window.clearInterval(retryUntilReady);
      }
    }, 50);

    sync();

    return () => {
      surfaceObserver.disconnect();
      shadowObserver?.disconnect();
      window.removeEventListener(
        "horizon-entry-point-ready",
        handleEntryPointReady
      );
      window.clearInterval(retryUntilReady);

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col w-full">
      <Toaster />

      {/* Header */}
      <header className="layout-container flex justify-between items-center py-8 md:py-6 xl:py-8">
        <Link to="/">
          <HorizonLogo />
        </Link>

        <div className="flex items-center gap-4">
          <Link to="/" className="btn-secondary hover:!bg-white">
            Login
          </Link>
          <Link to="/" className="btn-primary">
            Start for free
          </Link>
        </div>
      </header>

      <main className="layout-container flex-1 flex pb-8 xl:pb-12 2xl:pb-11">
        <div
          ref={surfaceRef}
          className={`relative w-full flex-1 ${
            surfaceBackground ? "" : DEFAULT_BACKGROUND_CLASS
          } flex items-center justify-start py-8 2xl:py-10 pl-8 2xl:pl-10 pr-8 md:pr-0 rounded-[32px] 2xl:rounded-[40px] overflow-hidden`}
          style={
            surfaceBackground
              ? { backgroundColor: surfaceBackground }
              : undefined
          }
        >
          {children}
        </div>
      </main>
    </div>
  );
}